/**
 * Header of the A/B results page: back, name, status chip ("Running · day N"),
 * the facts line (visitors, split, enough data, page), and the actions. Stop test is a neutral
 * outline button (red belongs in its confirm dialog). Stop test (running) and Resume (stopped) show
 * for every signed-in user; share-link viewers (`readOnly`) get Share and
 * Export only.
 */

import { HugeiconsIcon } from "@hugeicons/react"
import {
  Archive01Icon,
  ArrowLeft01Icon,
  FileDownloadIcon,
  Link01Icon,
  MoreHorizontalIcon,
  PlayIcon,
  StopIcon,
} from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

const topButton = "h-[34px] rounded-lg border-border bg-card px-3 text-xs font-medium text-foreground"

export function DetailHeader({
  name,
  statusChip,
  meta,
  isActive,
  isPaused,
  readOnly,
  onBack,
  onStop,
  onResume,
  onShare,
  onExport,
  onArchive,
  stopping = false,
  resuming = false,
}: {
  name: string
  statusChip: { label: string; dot: string }
  meta: ReactNode
  isActive: boolean
  isPaused: boolean
  readOnly: boolean
  onBack: () => void
  onStop: () => void
  onResume: () => void
  onShare: () => void
  onExport: () => void
  onArchive: () => void
  stopping?: boolean
  resuming?: boolean
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-2">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to experiments" className="-ml-2 size-7 shrink-0 text-muted-foreground">
          <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
        </Button>
        <div className="flex min-w-0 flex-col gap-[10px]">
          <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-[22px] items-center gap-[6px] rounded-md bg-muted px-2">
              <span className={cn("size-[6px] shrink-0 rounded-full", statusChip.dot)} />
              <span className="text-xs font-medium leading-4 text-foreground">{statusChip.label}</span>
            </span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-4 text-ui-secondary tabular-nums">{meta}</span>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isActive && !readOnly && (
          <Button
            variant="outline"
            onClick={onStop}
            disabled={stopping}
            className={topButton}
          >
            <HugeiconsIcon icon={StopIcon} size={13} />
            Stop test
          </Button>
        )}
        {isPaused && !readOnly && (
          <Button variant="outline" onClick={onResume} disabled={resuming} className={topButton}>
            <HugeiconsIcon icon={PlayIcon} size={13} />
            Resume
          </Button>
        )}
        <Button variant="outline" onClick={onShare} className={topButton}>
          <HugeiconsIcon icon={Link01Icon} size={13} />
          Share
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="size-[34px] rounded-lg border-border bg-card text-foreground" aria-label="More actions">
              <HugeiconsIcon icon={MoreHorizontalIcon} size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport}>
              <HugeiconsIcon icon={FileDownloadIcon} size={16} />
              Export CSV
            </DropdownMenuItem>
            {!readOnly && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onArchive}>
                  <HugeiconsIcon icon={Archive01Icon} size={16} />
                  Archive
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
